import { useRequest } from 'ahooks'
import { Button, Descriptions, Table, Tag, message } from 'antd'
import { forIn, groupBy } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DisabledRelationStatus } from '@/modules/production-project/production-detail/production-summary'
import { Production } from '@/modules/production-project/production.schema'
import { PpRelationAuditStatus, Resource, ResourceType } from '@/schemas'

import { getRelationCreatedFailedMessage } from '../relations-creator.component'
import { getNameLink } from '../special-project-relations.component'
import { UserAvatar } from '../user-name.component'

const getStatusTagColor = (status: PpRelationAuditStatus) => {
  let color = 'text-red-500'
  switch (status) {
    case PpRelationAuditStatus.审批中:
      color = 'text-orange-500'
      break
    case PpRelationAuditStatus.审批通过:
      color = 'text-green-500'
      break
  }
  return color
}

export const ResourceAudit = React.forwardRef<any, { selfId: string; type: ResourceType; resource?: Resource }>(
  ({ selfId, type, resource }, ref) => {
    const { t } = useTranslation()
    const [tableData, setTableData] = React.useState<any[]>([])
    const [pagination, setPagination] = React.useState({
      pageSize: 10,
      current: 1,
      total: 10,
    })
    const { data, refresh, loading } = useRequest<{ audits: any[]; total: number }>(
      {
        url: '/api/relations/getAuditsParentView',
        params: {
          selfId,
          type,
          ...pagination,
          page: pagination.current,
          // 不需要 cycleId, 获取该项目关联的所以 OKR
        },
      },
      {
        refreshDeps: [selfId, pagination.current, pagination.pageSize],
        onSuccess: (data) => {
          setPagination({ ...pagination, total: data.total })
        },
      }
    )

    const { run, loading: updateLoading } = useRequest<any[]>(
      ({ _selfId, _type, status }) => ({
        url: '/api/relations/updateAudit',
        method: 'post',
        data: {
          selfId: _selfId,
          type: _type,
          parentSelfId: selfId,
          parentType: type,
          status,
        },
      }),
      {
        refreshDeps: [selfId],
        manual: true,
        onSuccess: (data) => {
          const errors = data.errors || []
          if (errors.length) {
            for (const error of errors) {
              const config = getRelationCreatedFailedMessage({ error, onClose: message.destroy, t })
              message.error({
                ...config,
                duration: 0,
              })
            }
          }
          refresh()
        },
      }
    )

    React.useImperativeHandle(ref, () => ({
      auditRefresh: refresh,
    }))

    const onClickAudit = ({ selfId, type, isOk }: { selfId: string; type: ResourceType; isOk: boolean }) => {
      run({
        _selfId: selfId,
        _type: type,
        status: isOk ? PpRelationAuditStatus.审批通过 : PpRelationAuditStatus.审批拒绝,
      })
    }

    const productionSpecialDisabled =
      type === ResourceType.PRODUCTION && resource
        ? DisabledRelationStatus.includes((resource as Production).flowStatus)
        : false

    const columns = [
      {
        title: t('name'),
        key: 'name',
        render: (_: any, record: any) => getNameLink({ name: record.name, type: record.type, selfId: record.selfId }),
      },
      {
        title: t('type'),
        key: 'type',
        render: (_: any, record: any) =>
          record.type === 0 ? (
            <Tag color='blue'>{ResourceType[record.type]}</Tag>
          ) : (
            <Tag color='green'>{ResourceType[record.type]}</Tag>
          ),
      },
      {
        title: t('audit.auditStatus'),
        key: 'auditStatus',
        render: (_: any, record: any) => (
          <span className={`${getStatusTagColor(record.auditStatus)}`}>
            {PpRelationAuditStatus[record.auditStatus]}
          </span>
        ),
      },
      {
        title: 'Action',
        width: 200,
        render: (_: any, record: any) => {
          return (
            record.auditStatus === PpRelationAuditStatus.审批中 && (
              <>
                <Button
                  type='primary'
                  size='small'
                  danger
                  onClick={() => onClickAudit({ selfId: record.selfId, type: record.type, isOk: false })}
                >
                  拒绝
                </Button>
                <Button
                  type='primary'
                  size='small'
                  className='ml-2'
                  disabled={productionSpecialDisabled}
                  onClick={() => onClickAudit({ selfId: record.selfId, type: record.type, isOk: true })}
                >
                  通过
                </Button>
              </>
            )
          )
        },
      },
    ]

    React.useEffect(() => {
      const dataBySelfId = groupBy(data?.audits, 'selfId')
      const tableData: any[] = []
      forIn(dataBySelfId, (value, key) => {
        const latest = value.sort((a, b) => (moment(b.updatedAt).isBefore(a.updatedAt) ? -1 : 1))[0]
        tableData.push({
          key,
          name: latest.project?.name || latest.portfolio?.name,
          selfId: latest.selfId,
          type: latest.type,
          auditStatus: latest.auditStatus,
          history: value.map((v) => ({
            ...v,
            key: v.updatedAt,
          })),
        })
      })
      setTableData(tableData)
    }, [data])

    return (
      <Table
        loading={loading || updateLoading}
        columns={columns}
        expandable={{
          expandedRowRender: (record) => {
            const values = record.history.sort((a: any, b: any) => (moment(b.updatedAt).isBefore(a.updatedAt) ? 1 : -1))
            return values.map((other: any, index: number) => (
              <Descriptions className='ml-12' contentStyle={{ width: 200 }} key={index} bordered size='small'>
                {/* <Descriptions.Item label={<span className='flex items-center h-full'>{t('audit.applicantEmail')}</span>}>
                <UserAvatar email={other.applicantEmail} showName />
              </Descriptions.Item> */}
                {/* <Descriptions.Item label={<span className='flex items-center h-full'>{t('audit.approverEmail')}</span>}>
                <UserAvatar email={other.approverEmail} showName />
              </Descriptions.Item> */}
                <Descriptions.Item label={<span className='flex items-center h-full'>{t('audit.operatorEmail')}</span>}>
                  <UserAvatar email={other.operatorEmail} showName />
                </Descriptions.Item>
                <Descriptions.Item label={t('audit.auditStatus')}>
                  <span className={`${getStatusTagColor(other.auditStatus)}`}>
                    {PpRelationAuditStatus[other.auditStatus]}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t('audit.createdAt')}>
                  {moment(other.createdAt).utc().format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
              </Descriptions>
            ))
          },
          rowExpandable: (record) => record.history?.length > 1,
        }}
        dataSource={tableData.sort((a) => (a.auditStatus === 1 ? -1 : 1))}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize, total: pagination.total })
          },
        }}
      />
    )
  }
)
